import * as XLSX from 'xlsx';
import * as ExcelJS from 'exceljs';

export type ReportExcelCellValue = string | number | Date | { text: string; hyperlink: string } | null | undefined;

const REPORT_TITLE_FILL: ExcelJS.Fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF000000' } };
const REPORT_HEADER_FILL: ExcelJS.Fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF00008B' } };
const REPORT_DATE_FORMAT = 'dd/mm/yyyy hh:mm:ss AM/PM';
const REPORT_NUMBER_FORMAT = '#,##0.00';
const REPORT_HEADER_ROW = 5;
const CENTER_ALIGNMENT: Partial<ExcelJS.Alignment> = { horizontal: 'center' };
const RIGHT_ALIGNMENT: Partial<ExcelJS.Alignment> = { horizontal: 'right' };
const LEFT_ALIGNMENT: Partial<ExcelJS.Alignment> = { horizontal: 'left' };

export class ExcelUtilities {
  static exportSimpleDataToExcel(title: string, headers: string[], rows: string[][]): void {
    const data = rows;

    const workSheet = XLSX.utils.aoa_to_sheet([headers, ...data]);
    workSheet['!cols'] = [{ wpx: 150 }, { wpx: 120 }, { wpx: 100 }, { wpx: 100 }, { wpx: 200 }];

    const workBook: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workBook, workSheet, 'Hoja1');
    XLSX.writeFile(workBook, `${title}.xlsx`);
  }

  static async exportReportExcel( title: string, headers: string[], rows: ReportExcelCellValue[][], client: string
  ): Promise<void> {
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet(this.toSafeSheetName(title));

    this.writeReportInfoBlock(sheet, client);

    this.writeHeaderRow(sheet, headers);
    this.writeDataRows(sheet, headers, rows);

    this.writeAutoFilter(sheet, headers, rows);
    this.applyColumnWidths(sheet, headers, rows);

    const buffer = await workbook.xlsx.writeBuffer();
    this.downloadBlob(buffer, `${title}.xlsx`);
  }

  private static writeReportInfoBlock(sheet: ExcelJS.Worksheet, client: string): void {
    sheet.mergeCells('A1:B1');
    const titleCell = sheet.getCell('A1');
    titleCell.value = 'Reporte';
    titleCell.font = { color: { argb: 'FFFFFFFF' } };
    titleCell.fill = REPORT_TITLE_FILL;
    titleCell.alignment = CENTER_ALIGNMENT;

    sheet.getCell('A2').value = 'Cliente';
    const clientCell = sheet.getCell('B2');
    clientCell.value = client;
    clientCell.alignment = RIGHT_ALIGNMENT;
  }

  private static writeHeaderRow(sheet: ExcelJS.Worksheet, headers: string[]): void {
    const headerRow = sheet.getRow(REPORT_HEADER_ROW);
    headers.forEach((header, index) => {
      const cell = headerRow.getCell(index + 1);
      cell.value = header;
      cell.fill = REPORT_HEADER_FILL;
      cell.font = { color: { argb: 'FFFFFFFF' } };
      cell.alignment = CENTER_ALIGNMENT;
    });
    headerRow.commit();
  }

  private static writeDataRows(sheet: ExcelJS.Worksheet, headers: string[], rows: ReportExcelCellValue[][]): void {
    rows.forEach((row, rowIndex) => {
      const excelRow = sheet.getRow(REPORT_HEADER_ROW + 1 + rowIndex);
      headers.forEach((_header, columnIndex) => {
        const value = row[columnIndex];
        const cell = excelRow.getCell(columnIndex + 1);
        cell.alignment = LEFT_ALIGNMENT;

        if (value != null && typeof value === 'object' && 'hyperlink' in value) {
          cell.value = { text: value.text, hyperlink: value.hyperlink };
          cell.font = { color: { argb: 'FF0000FF' }, underline: true };
          cell.alignment = LEFT_ALIGNMENT;
        } else if (value instanceof Date) {
          if (isNaN(value.getTime())) {
            cell.value = 'Fecha inválida';
          } else {
            cell.value = value;
            cell.numFmt = REPORT_DATE_FORMAT;
          }
        } else if (typeof value === 'number') {
          if (isNaN(value)) {
            cell.value = '';
          } else {
            cell.value = value;
            cell.numFmt = REPORT_NUMBER_FORMAT;
          }
        } else {
          cell.value = value ?? '';
        }
      });
      excelRow.commit();
    });
  }

  private static writeAutoFilter(sheet: ExcelJS.Worksheet, headers: string[], rows: ReportExcelCellValue[][]): void {
    sheet.autoFilter = {
      from: { row: REPORT_HEADER_ROW, column: 1 },
      to: { row: REPORT_HEADER_ROW + rows.length, column: headers.length }
    };
  }

  private static applyColumnWidths(sheet: ExcelJS.Worksheet, headers: string[], rows: ReportExcelCellValue[][]): void {
    headers.forEach((header, columnIndex) => {
      sheet.getColumn(columnIndex + 1).width = 25;
    });
  }

  private static toSafeSheetName(title: string): string {
    const sanitized = title.replace(/[\\/*?:[\]]/g, ' ').trim();
    return (sanitized || 'Reporte').substring(0, 31);
  }

  private static downloadBlob(buffer: ExcelJS.Buffer, fileName: string): void {
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  static parseDate(value: string): ReportExcelCellValue {
    if (!value) 
      return value;
    const date = new Date(value);
    return isNaN(date.getTime()) ? value : date;
  }

  static getGoogleMapsURL(lat: any, long: any): { text: string; hyperlink: string } {
    return {
      text: `https://www.google.com/maps/search/?api=1&query=${lat},${long}`,
      hyperlink: `https://www.google.com/maps/search/?api=1&query=${lat},${long}`
    };
  }

  static max6DecTruncate(value: string): string {
    const normalized = value.replace(',', '.');
    const [entero, decimal = ''] = normalized.split('.');
    return decimal ? `${entero},${decimal.slice(0, 6)}` : entero;
  }
}
